import { useCreateService } from "@/components/services/createServices";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

import { z } from "zod";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { fetchBuildings } from "../buildings/getBuilding";
import { Controller } from "react-hook-form";

interface ServiceFormProps {
  closeDialog: () => void; // Function to close dialog
}

const ServiceForm = ({ closeDialog }: ServiceFormProps) => {
  const {
    control,
    handleSubmit,
    register,

    isSubmitting,
    createServiceFn,
    companyData,

    createServiceSchema,
    reset,
  } = useCreateService();

  type CreateServiceType = z.infer<typeof createServiceSchema>;

  const handleCreateService = async (data: CreateServiceType) => {
    if (!companyData) {
      toast.error("Company information not available");
      return;
    }

    const { status } = await createServiceFn({
      serviceName: data.serviceName,
      serviceQuantity: data.serviceQuantity,
      price: data.price,
      buildingsId: data.buildingsId || undefined,
      companyId: companyData.company[0].id,
    });

    if (status === 201) {
      reset();
      closeDialog();
    }
  };

  const { buildings } = fetchBuildings();

  return (
    <form onSubmit={handleSubmit(handleCreateService)}>
      <div>
        <div className="flex space-x-4">
          <div className="w-full">
            <label
              htmlFor="serviceName"
              className="block text-sm font-medium text-black"
            >
              Service Name
            </label>
            <Input
              {...register("serviceName", {
                required: "Expense description is required",
              })}
              type="text"
              id="serviceName"
              autoComplete="off"
              className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
            />
          </div>
        </div>
        <div className="mt-4 flex space-x-4">
          <div className="flex w-1/3 flex-col">
            <label
              htmlFor="serviceQuantity"
              className="block text-sm font-medium text-black"
            >
              Service Quantity
            </label>
            <Input
              {...register("serviceQuantity")}
              type="number"
              id="serviceQuantity"
              autoComplete="off"
              className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
            />
          </div>
          <div className="flex w-1/3 flex-col">
            <label
              htmlFor="price"
              className="block text-sm font-medium text-black"
            >
              Price
            </label>
            <Input
              {...register("price")}
              type="number"
              id="price"
              autoComplete="off"
              className="mt-1 block w-full rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm"
            />
          </div>
          <div className="flex w-1/3 flex-col">
            <label
              htmlFor="building"
              className="block text-sm font-medium text-black"
            >
              Assign to Customer
            </label>
            <Controller
              name="buildingsId"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Select
                  onValueChange={(value) => {
                    field.onChange(value); // Update the form state
                  }}
                  value={field.value || ""} // Use the form state value
                >
                  <SelectTrigger className="mt-1 w-36 rounded-md border-zinc-300 px-3 py-2 text-zinc-900 shadow-sm focus:border-zinc-500 focus:ring-zinc-500 sm:text-sm">
                    <SelectValue placeholder="Customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {buildings?.buildings.map((building) => (
                      <SelectItem key={building.id} value={building.id}>
                        {building.buildingName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>
        <div className="mt-4">
          <Button
            disabled={isSubmitting}
            className="w-full bg-[#16A34A] hover:bg-[#1bc459]"
            type="submit"
          >
            Create Service
          </Button>
        </div>
      </div>
    </form>
  );
};

export default ServiceForm;
